import apiFetch from '@scale/frontend-lib/api-fetch';

// Caching the request promise itself so that if there are concurrent requests for the same key,
// they could await the original promise rather than submitting redundant requests.
const requestCache = new Map<string, Promise<boolean | UnleashVariant>>();

const localStorage = typeof window === 'undefined' ? null : window.localStorage;

// Feature flags are often used as kill switches
// to reduce the time it takes to mitigate incidents.
// This is now set to 30 seconds (it's also less confusing when developing locally).
const SESSION_CACHE_EXPIRY_MS = 30 * 1000;

type CachedValue = {
  value: boolean | UnleashVariant;
  expiry: number;
};

// Copied from: https://github.com/Unleash/unleash-client-node/blob/main/src/variant.ts
export type UnleashVariant = {
  name: string;
  enabled: boolean;
  payload?: { type: 'string' | 'json' | 'csv'; value: string };
  feature_enabled?: boolean;
};

export const cachedFetch = async <T extends boolean | UnleashVariant>(
  url: string,
  context: Record<string, any> | null = null,
): Promise<T> => {
  let localStorageKey = `unleash-${url}`;
  const userId = context?.user?.id || context?.user?._id;
  // add userId identity to specify flag to a single user
  if (userId) {
    localStorageKey += `-${userId}`;
  }

  let cachedRequest = requestCache.get(localStorageKey);
  if (cachedRequest) {
    return cachedRequest as Promise<T>;
  }

  const storageValue = JSON.parse(localStorage?.getItem(localStorageKey) ?? 'null') as CachedValue;
  if (storageValue) {
    if (storageValue.expiry > Date.now()) {
      return storageValue.value as T;
    } else {
      localStorage?.removeItem(localStorageKey);
    }
  }

  cachedRequest = apiFetch<T>(context, url, { redirectOnUnauthorized: true });
  requestCache.set(localStorageKey, cachedRequest);

  return cachedRequest.then(value => {
    const cachedValue = { value, expiry: Date.now() + SESSION_CACHE_EXPIRY_MS };
    try {
      localStorage?.setItem(localStorageKey, JSON.stringify(cachedValue));
    } catch (e: unknown) {
      if (e instanceof Error) {
        // Clear local storage that was corrupted either by too many feature flags or
        // a bad value set.
        // Don't throw an error since local storage is mainly used for caching.
        // eslint-disable-next-line no-console
        console.warn(e);
        localStorage?.clear();
        localStorage?.setItem(localStorageKey, JSON.stringify(cachedValue));
      }
    }
    return value;
  }) as Promise<T>;
};

export type UnleashFeatureFlagEnabledArgs = {
  feature: string;
  querystring?: string;
  context?: Record<string, any> | null;
};

export async function unleashFeatureFlagEnabled({
  feature,
  querystring,
  context,
}: UnleashFeatureFlagEnabledArgs) {
  let url = `internal/feature/unleash/${feature}`;
  if (querystring) {
    url += `?${querystring}`;
  }
  return cachedFetch<boolean>(url, context);
}

export async function unleashFeatureFlagVariant({
  feature,
  querystring,
  context,
}: UnleashFeatureFlagEnabledArgs): Promise<UnleashVariant> {
  let url = `internal/feature/unleash_variant/${feature}`;
  if (querystring) {
    url += `?${querystring}`;
  }
  return cachedFetch<UnleashVariant>(url, context);
}
